import { supabase } from '../SupabaseClient/supabase-client';

export type ContributionRecommendationRecord = {
  id: string;
  ticker: string;
  assetClassCode?: string;
  assetClassId?: string;
  assetClassName?: string;
  assetClassColor?: string;
  rating: number;
  price: number;
  quantity: number;
  suggestedValue: number;
};

export type ContributionsResult = {
  totalDistributed: number;
  totalQuantity: number;
  totalCashRemainder?: number;
  assetClassesCount?: number;
  eligibleAssetsCount?: number;
  recommendations: ContributionRecommendationRecord[];
};

export type SavedContributionRecord = {
  id: string;
  amount: number;
  totalDistributed: number;
  totalQuantity: number;
  totalCashRemainder: number;
  assetClassesCount?: number;
  eligibleAssetsCount?: number;
  recommendations: ContributionRecommendationRecord[];
  createdAt?: string;
};

const CONTRIBUTIONS_TABLE = 'contributions';

const toNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toString = (value: unknown) => (typeof value === 'string' ? value : '');

const getOptionalNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    const parsed = toNumber(value, Number.NaN);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

let cachedUserId: string | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

const getUserId = async (): Promise<string> => {
  if (cachedUserId) return cachedUserId;

  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    cachedUserId = null;
    throw new Error('Usuário não autenticado');
  }

  cachedUserId = data.user.id;
  return cachedUserId;
};

const mapResponse = (input: unknown): ContributionsResult => {
  const payload = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;

  const planPayload = payload.plan && typeof payload.plan === 'object' ? (payload.plan as Record<string, unknown>) : null;
  const planItems = Array.isArray(payload.plan)
    ? payload.plan
    : Array.isArray(payload.classes)
      ? payload.classes
      : Array.isArray(planPayload?.classes)
        ? planPayload?.classes
        : [];
  const recommendations: ContributionRecommendationRecord[] = [];
  let totalDistributed = 0;
  let totalQuantity = 0;
  const initialCashRemainder = getOptionalNumber(
    planPayload?.totalCashRemainder,
    planPayload?.cashRemainder,
    payload.totalCashRemainder,
    payload.cashRemainder,
    payload.cash_remainder,
    payload.remainder,
  );
  let totalCashRemainder = initialCashRemainder ?? 0;

  planItems.forEach((planItemRaw) => {
    if (!planItemRaw || typeof planItemRaw !== 'object') return;
    const planItem = planItemRaw as Record<string, unknown>;
    const classId = toString(planItem.assetClassId ?? planItem.id);
    const classCode = toString(planItem.code);
    const className = toString(planItem.className ?? planItem.assetClassName ?? classCode);
    const classColor = toString(planItem.classColor ?? planItem.assetClassColor);
    const aporteClasse = toNumber(planItem.aporteClasse);
    if (initialCashRemainder === undefined) {
      totalCashRemainder += toNumber(planItem.cashRemainder);
    }
    if (Number.isFinite(aporteClasse)) {
      totalDistributed += aporteClasse;
    }

    const assets = Array.isArray(planItem.assets) ? planItem.assets : [];
    assets.forEach((assetRaw) => {
      if (!assetRaw || typeof assetRaw !== 'object') return;
      const asset = assetRaw as Record<string, unknown>;
      const ticker = toString(asset.ticker ?? asset.symbol ?? asset.asset);
      const id = toString(asset.targetAssetId ?? asset.id ?? ticker);
      if (!id && !ticker) return;

      const price = toNumber(asset.price ?? asset.unitPrice ?? asset.currentPrice);
      const quantity = toNumber(asset.quantityToBuy ?? asset.quantity ?? asset.qty);
      const suggestedValue = toNumber(
        asset.invested ??
          asset.aporteAllocated ??
          asset.aporteAtivo ??
          asset.missingValue ??
          asset.suggestedValue ??
          asset.value ??
          asset.amount ??
          asset.allocation ??
          asset.contribution,
      );

      if (Number.isFinite(quantity)) {
        totalQuantity += quantity;
      }

      recommendations.push({
        id: id || ticker,
        ticker: ticker || id,
        assetClassId: classId || undefined,
        assetClassCode: classCode || undefined,
        assetClassName: className || undefined,
        assetClassColor: classColor || undefined,
        rating: toNumber(asset.rating),
        price,
        quantity,
        suggestedValue,
      });
    });
  });

  const fallbackRecommendations = Array.isArray(payload.recommendations)
    ? payload.recommendations
    : Array.isArray(payload.items)
      ? payload.items
      : Array.isArray(payload.data)
        ? payload.data
        : [];

  const normalizedRecommendations =
    recommendations.length > 0
      ? recommendations
      : (fallbackRecommendations as ContributionRecommendationRecord[]);

  return {
    totalDistributed:
      totalDistributed ||
      toNumber(
        payload.totalDistributed ??
          payload.total_distributed ??
          payload.distributedAmount ??
          payload.total_value ??
          planPayload?.totalDistributed,
      ),
    totalCashRemainder:
      totalCashRemainder ||
      toNumber(payload.totalCashRemainder ?? payload.cashRemainder ?? payload.cash_remainder ?? payload.remainder) ||
      getOptionalNumber(payload.remainingCash),
    totalQuantity:
      totalQuantity ||
      toNumber(payload.totalQuantity ?? payload.total_quantity ?? payload.estimatedQuantity),
    assetClassesCount:
      planItems.length > 0
        ? planItems.length
        : getOptionalNumber(
            payload.assetClassesCount,
            payload.asset_classes_count,
            payload.classesCount,
            payload.classes_count,
          ),
    eligibleAssetsCount:
      normalizedRecommendations.length > 0
        ? normalizedRecommendations.length
        : getOptionalNumber(
            payload.eligibleAssetsCount,
            payload.eligible_assets_count,
            payload.assetsCount,
            payload.assets_count,
          ),
    recommendations: normalizedRecommendations as ContributionRecommendationRecord[],
  };
};

const normalizeRecommendationForInsert = (recommendation: ContributionRecommendationRecord) => ({
  id: recommendation.id,
  ticker: recommendation.ticker.toUpperCase(),
  asset_class_code: recommendation.assetClassCode ?? null,
  asset_class_id: recommendation.assetClassId ?? null,
  asset_class_name: recommendation.assetClassName ?? null,
  asset_class_color: recommendation.assetClassColor ?? null,
  rating: toNumber(recommendation.rating, 0),
  price: toNumber(recommendation.price, 0),
  quantity: toNumber(recommendation.quantity, 0),
  suggested_value: toNumber(recommendation.suggestedValue, 0),
});

const buildContributionInsert = (input: { amount: number; result: ContributionsResult }) => {
  const recommendations = Array.isArray(input.result.recommendations) ? input.result.recommendations : [];
  const assetClassesCount = getOptionalNumber(input.result.assetClassesCount);
  const eligibleAssetsCount = getOptionalNumber(input.result.eligibleAssetsCount);

  return {
    amount: toNumber(input.amount, 0),
    total_distributed: toNumber(input.result.totalDistributed, 0),
    total_quantity: toNumber(input.result.totalQuantity, 0),
    total_cash_remainder: toNumber(input.result.totalCashRemainder, 0),
    asset_classes_count: assetClassesCount ?? null,
    eligible_assets_count: eligibleAssetsCount ?? null,
    recommendations: recommendations.map(normalizeRecommendationForInsert),
  };
};

const mapStoredRecommendation = (input: unknown): ContributionRecommendationRecord | null => {
  if (!input || typeof input !== 'object') return null;
  const value = input as Record<string, unknown>;
  const id = toString(value.id ?? value.target_asset_id);
  const ticker = toString(value.ticker);
  if (!id && !ticker) return null;

  return {
    id: id || ticker,
    ticker: ticker.toUpperCase() || id,
    assetClassId: toString(value.asset_class_id ?? value.assetClassId) || undefined,
    assetClassCode: toString(value.asset_class_code ?? value.assetClassCode) || undefined,
    assetClassName: toString(value.asset_class_name ?? value.assetClassName) || undefined,
    assetClassColor: toString(value.asset_class_color ?? value.assetClassColor) || undefined,
    rating: toNumber(value.rating, 0),
    price: toNumber(value.price, 0),
    quantity: toNumber(value.quantity, 0),
    suggestedValue: toNumber(value.suggested_value ?? value.suggestedValue, 0),
  };
};

export const mapSavedContributionRow = (
  row:
    | {
        id?: string | number;
        amount?: number;
        total_distributed?: number;
        total_quantity?: number;
        total_cash_remainder?: number;
        asset_classes_count?: number;
        eligible_assets_count?: number;
        recommendations?: unknown;
        created_at?: string;
      }
    | null,
): SavedContributionRecord | null => {
  if (!row) return null;
  const id = typeof row.id === 'string' ? row.id : row.id !== undefined ? String(row.id) : '';
  if (!id) return null;

  const recommendationsRaw = Array.isArray(row.recommendations) ? row.recommendations : [];
  const recommendations = recommendationsRaw
    .map(mapStoredRecommendation)
    .filter(Boolean) as ContributionRecommendationRecord[];

  return {
    id,
    amount: toNumber(row.amount, 0),
    totalDistributed: toNumber(row.total_distributed, 0),
    totalQuantity: toNumber(row.total_quantity, 0),
    totalCashRemainder: toNumber(row.total_cash_remainder, 0),
    assetClassesCount: getOptionalNumber(row.asset_classes_count) ?? undefined,
    eligibleAssetsCount: getOptionalNumber(row.eligible_assets_count) ?? undefined,
    recommendations,
    createdAt: toString(row.created_at) || undefined,
  };
};

export const contributionsService = {
  async simulate(amount: number): Promise<ContributionsResult> {
    if (!Number.isFinite(amount)) {
      throw new Error('Valor de aporte invalido');
    }

    const { data, error } = await supabase.functions.invoke('contributions', {
      body: { amount },
    });

    if (error) {
      throw error;
    }

    return mapResponse(data);
  },

  async listRecent(limit = 5): Promise<SavedContributionRecord[]> {
    const userId = await getUserId();

    const { data, error } = await supabase
      .from(CONTRIBUTIONS_TABLE)
      .select(
        'id,amount,total_distributed,total_quantity,total_cash_remainder,asset_classes_count,eligible_assets_count,recommendations,created_at',
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw error;
    }

    return (data || []).map(mapSavedContributionRow).filter(Boolean) as SavedContributionRecord[];
  },

  async save(input: { amount: number; result: ContributionsResult }): Promise<SavedContributionRecord> {
    if (!input || !input.result) {
      throw new Error('Dados do aporte ausentes');
    }

    const userId = await getUserId();
    const payload = buildContributionInsert(input);

    const { data, error } = await supabase
      .from(CONTRIBUTIONS_TABLE)
      .insert({ ...payload, user_id: userId })
      .select(
        'id,amount,total_distributed,total_quantity,total_cash_remainder,asset_classes_count,eligible_assets_count,recommendations,created_at',
      )
      .single();

    if (error) {
      throw error;
    }

    const mapped = mapSavedContributionRow(data);
    if (!mapped) {
      throw new Error('Registro de aporte invalido');
    }

    return mapped;
  },

  async consolidatePortfolio(contributionId?: string): Promise<void> {
    const options = contributionId ? { body: { contributionId } } : {};
    const { error } = await supabase.functions.invoke('consolidate-portfolio', options);
    if (error) {
      throw error;
    }
  },
};
